import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Pencil, Plus, X } from "lucide-react";

const VIOLET = "#B0863C";
const TYPES = ["admin", "super_admin", "collaborator", "partner", "reserved"];

export const SplitEditor = ({ splits, onSaved }) => {
  const [open, setOpen] = useState(false);
  const alloc = splits?.allocations || [];
  const platform0 = alloc.find((a) => a.type === "platform")?.percentage ?? 50;
  const recips0 = () => alloc.filter((a) => a.type !== "platform").map((a) => ({
    recipient_email: a.recipient, recipient_type: a.type, percentage: a.percentage,
  }));
  const [versionName, setVersionName] = useState("");
  const [reason, setReason] = useState("");
  const [effective, setEffective] = useState("");
  const [platform, setPlatform] = useState(platform0);
  const [recips, setRecips] = useState(recips0);
  const [saving, setSaving] = useState(false);

  const total = Number(platform) + recips.reduce((s, r) => s + Number(r.percentage || 0), 0);
  const rounded = Math.round(total * 100) / 100;
  const setField = (i, k, v) => setRecips((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const addRecipient = () => setRecips((rs) => [...rs, { recipient_email: "", recipient_type: "admin", percentage: 0 }]);
  const removeRecipient = (i) => setRecips((rs) => rs.filter((_, j) => j !== i));

  const save = async () => {
    if (rounded !== 100) { toast.error(`Percentages must total 100 (now ${rounded})`); return; }
    if (!versionName.trim() || !reason.trim()) { toast.error("Version name and reason are required"); return; }
    const cleaned = recips
      .map((r) => ({ recipient_email: r.recipient_email.trim(), recipient_type: r.recipient_type, percentage: Number(r.percentage) }))
      .filter((r) => r.recipient_email);
    if (cleaned.some((r) => r.recipient_type !== "reserved" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(r.recipient_email))) {
      toast.error("Non-reserved recipients need a valid email"); return;
    }
    const emails = cleaned.filter((r) => r.recipient_type !== "reserved").map((r) => r.recipient_email.toLowerCase());
    if (new Set(emails).size !== emails.length) { toast.error("Duplicate recipient email"); return; }
    setSaving(true);
    try {
      await api.post("/admin/splits/rules", {
        version_name: versionName, reason, effective_at: effective || undefined,
        platform_percentage: Number(platform), recipients: cleaned,
      });
      toast.success("New split rule activated");
      setOpen(false); onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save split rule");
    } finally { setSaving(false); }
  };

  const reset = () => { setPlatform(platform0); setRecips(recips0()); setVersionName(""); setReason(""); setEffective(""); };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="edit-split-btn" size="sm" className="rounded-full font-semibold" style={{ background: VIOLET }}>
          <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit split rule
        </Button>
      </DialogTrigger>
      <DialogContent className="border-[#222430] max-w-xl" style={{ background: "#14151C" }}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-white">New split rule version</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">Add, rename or remove recipients (rename an "Open slot" to a partner's email to fill it). Percentages must total 100%. Creates a versioned rule with an audit trail.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-mono text-[#71717A]">Version name</label>
              <Input data-testid="split-version-input" value={versionName} onChange={(e) => setVersionName(e.target.value)} placeholder="Revenue split v4" className="mt-1.5 bg-black/30 border-[#222430] text-white" />
            </div>
            <div>
              <label className="label-mono text-[#71717A]">Effective date</label>
              <Input data-testid="split-effective-input" type="date" value={effective} onChange={(e) => setEffective(e.target.value)} className="mt-1.5 bg-black/30 border-[#222430] text-white" />
            </div>
          </div>
          <div className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2">
            <span className="text-white text-sm">Platform (retained)</span>
            <div className="flex items-center gap-1">
              <Input data-testid="split-platform-input" type="number" value={platform} onChange={(e) => setPlatform(e.target.value)} className="w-20 bg-black/30 border-[#222430] text-white text-right" />
              <span className="text-[#71717A]">%</span>
            </div>
          </div>
          {recips.map((r, i) => (
            <div key={i} className="flex items-center gap-2 bg-black/30 rounded-lg px-3 py-2">
              <Input data-testid={`split-recipient-email-${i}`} value={r.recipient_email} onChange={(e) => setField(i, "recipient_email", e.target.value)} placeholder="partner@email.com" className="flex-1 bg-black/30 border-[#222430] text-white text-sm" />
              <select data-testid={`split-recipient-type-${i}`} value={r.recipient_type} onChange={(e) => setField(i, "recipient_type", e.target.value)} className="bg-black/30 border border-[#222430] text-[#A1A1AA] text-xs rounded-md px-1.5 py-2">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <Input data-testid={`split-recipient-${i}`} type="number" value={r.percentage} onChange={(e) => setField(i, "percentage", e.target.value)} className="w-16 bg-black/30 border-[#222430] text-white text-right text-sm" />
              <span className="text-[#71717A] text-sm">%</span>
              <button data-testid={`split-remove-${i}`} onClick={() => removeRecipient(i)} className="text-[#71717A] hover:text-red-400 shrink-0"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <Button data-testid="split-add-recipient" onClick={addRecipient} size="sm" variant="ghost" className="rounded-full text-[#A1A1AA] hover:text-white border border-dashed border-[#222430]">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add recipient
          </Button>
          <div className="flex justify-between text-sm">
            <span className="text-[#A1A1AA]">Total</span>
            <span data-testid="split-total" className="font-display font-bold" style={{ color: rounded === 100 ? "#C8A15C" : "#FF5500" }}>{total}%</span>
          </div>
          <div>
            <label className="label-mono text-[#71717A]">Reason (audited)</label>
            <Textarea data-testid="split-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is the split changing?" className="mt-1.5 bg-black/30 border-[#222430] text-white resize-none" rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="split-save-btn" onClick={save} disabled={saving} className="rounded-full font-semibold" style={{ background: VIOLET }}>
            {saving ? "Saving…" : "Activate new rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
